// import dependencies
import React from 'react';
import styled from 'styled-components';
import Grid from 'components/Grid';
import { Text, Input, Button, Heading } from 'pcln-design-system';
import { themeGet } from '@styled-system/theme-get';
import { Event } from 'components/Tracking/index.js';
import axios from 'axios';
import { withAMP } from 'containers/AMP';

// constants
import { regexEmail } from '../../constants/';
// endpoint
import config from 'config';
import PropTypes from 'prop-types';

const NewsletterButton = styled(Button)`
    font-family: ${themeGet('font')};
    font-size: 14px;
    -webkit-font-smoothing: antialiased;
    font-weight: ${themeGet('fontWeights.bold')};
    width: 100%;
    // to match the height of the input
    min-height: 49px;
    padding: 0 ${themeGet('space.2')}px;
`;

const FixedFontSizeInput = styled(Input)`
    font-size: ${themeGet('fontSizes.2')}px;
`;

/* eslint-disable array-element-newline, space-infix-ops */
export class Newsletter extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: false,
            success: false,
            error: false,
            valid: false,
            validEmail: true,
            email: false
        };
    }

    /**
     * Handle the form submit
     * @param  {Object} event
     */
    submitForm = event => {
        event.preventDefault();

        if (this.state.valid && this.state.email) {
            this.setState({
                loading: true,
                validEmail: true
            });

            axios({
                url: config.NEWSLETTER_SIGNUP_URL,
                method: 'get',
                params: {
                    INIT_SESSION: false,
                    email: this.state.email,
                    sourceId: this.sourceRef.value
                }
            })
                .then(this.submitSuccess)
                .catch(this.submitError);
        } else {
            this.setState({
                validEmail: false,
                error: false,
                loading: false
            });
        }
    };

    /**
     * Handle the form submition success
     */
    submitSuccess = () => {
        this.setState({
            success: true,
            loading: false
        });
    };

    /**
     * Handle the form submition failure
     */
    submitError = () => {
        this.setState({
            error: true,
            loading: false
        });
    };

    /**
     * Validate email input
     * @param {Object} event
     */
    validateEmailInput = event => {
        if (event.target.value && regexEmail.test(event.target.value)) {
            this.setState({
                valid: true,
                email: event.target.value
            });
        } else {
            this.setState({ valid: false });
        }
    };

    render() {
        const { location, isAMP } = this.props;

        if (isAMP) {
            return null;
        }

        return (
            <div className="newsletter-container" data-props={JSON.stringify(this.props)}>
                {this.state.success ? (
                    <Heading fontSize={[2, 3]}>
                        <Event on="visible" category="SEO-HOTEL" action={`${location} Email Signup`} label="Valid" />
                        Congratulations! You’re on the list to receive exclusive access to priceline deals.
                    </Heading>
                ) : (
                    <form onSubmit={this.submitForm} method="get" action={`${config.NEWSLETTER_SIGNUP_URL}`}>
                        <input type="hidden" ref={el => (this.sourceRef = el)} value={this.props.sourceId} />
                        <Grid width={[1, 1, 1 / 2, 6 / 12]} px={2} mb={[3, 3, 0]}>
                            <Text.p m={0} fontSize={1} bold>
                                Sign up for email-only coupons, special offers and promotions — exclusively from Priceline.
                            </Text.p>
                        </Grid>

                        <Grid width={[1, 1 / 2, 1 / 4, 4 / 12]} px={[0, 2]} mb={[3, 3, 0]}>
                            <FixedFontSizeInput
                                onChange={this.validateEmailInput}
                                color={this.state.validEmail ? null : 'red'}
                                placeholder="Enter your email address"
                                id="newsletterEmail"
                                aria-label="Your Email"
                            />

                            {this.state.error && (
                                <Text.p color="red" mt={3} fontSize={0}>
                                    Sorry, there was a problem with your registration. Please try again in a few minutes.
                                </Text.p>
                            )}

                            {!this.state.validEmail && (
                                <Text.p color="red" mt={3} fontSize={0}>
                                    Please enter a valid email
                                    <Event
                                        on="visible"
                                        category="SEO-HOTEL"
                                        action={`${location} Email Signup`}
                                        label="Invalid"
                                    />
                                </Text.p>
                            )}
                        </Grid>

                        <Grid width={[1, 1 / 2, 1 / 4, 2 / 12]} px={[0, 2]} mb={[3, 3, 0]}>
                            <NewsletterButton aria-label="Subscribe" variation="outline">
                                {this.state.loading ? 'Submitting' : 'Send me deals'}
                            </NewsletterButton>
                        </Grid>
                    </form>
                )}
            </div>
        );
    }
}

Newsletter.propTypes = {
    sourceId: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    isAMP: PropTypes.bool
};

/* eslint-enable array-element-newline, space-infix-ops */
export default withAMP(Newsletter);
